<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { FormKitNode } from '@formkit/core'
import { createInput } from '@formkit/vue'
import TelInputScheleton from './TelInputScheleton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const customTelInput = createInput(TelInputScheleton)

const props = defineProps<{
  name: string
  required: boolean
  label?: string
  validation?: string
  validationMessages?: {
    [key: string]: string
  }
  id?: string
}>()

const attrs = useAttrs()
const hasValidation = ref(true)
const isFocused = ref(false)
const isFilled = ref(false)

const isValid = ref(false)
const phoneObject = ref<any>(null)

const customValidation = (node: FormKitNode) => {
  // If the field is empty and not required, it should be valid
  if (!node.value && !props.required) {
    return true
  }

  // If the field is empty and required, it should be invalid
  if (!node.value && props.required) {
    return false
  }

  // Use the latest validation state from vue-tel-input
  return isValid.value
}

const handleFocus = (f: boolean) => {
  isFocused.value = f
}

const handleValidate = (value: unknown, node?: FormKitNode) => {
  const valid = value as boolean
  isValid.value = valid
}

const handleInput = (value: unknown, node?: FormKitNode) => {
  const stringValue = value as string | undefined
  isFilled.value = stringValue !== '' && stringValue !== undefined
}

const handlePhoneObject = (obj: any) => {
  phoneObject.value = obj
  // Update isValid from the phone object for consistency
  if (obj && typeof obj === 'object' && 'isValid' in obj) {
    isValid.value = obj.isValid
  }
}

const validationString = computed(() => {
  const validationString = props.validation ?? ''
  const v = validationString.split('|')

  v.push('customValidation')
  if (props.required) {
    v.push('required')
  }

  return v.filter(Boolean).join('|')
})

const classes: Ref<string> = ref('')
watch(
  [isFilled, isFocused, hasValidation, () => attrs.class],
  () => {
    classes.value = [
      isFilled.value ? 'is-filled' : '',
      isFocused.value ? 'is-focused' : '',
      hasValidation.value ? 'has-validation' : '',
      attrs.class ?? '',
    ]
      .filter(Boolean)
      .join(' ')
  },
  { immediate: true },
)
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<FormKit
    :type="customTelInput"
    :id="id"
    :label="label ?? ''"
    :classes="{
      outer: `custom-tel-input ${classes}`,
    }"
    :validation-rules="{ customValidation }"
    :validation="validationString ?? ''"
    :validation-messages="{
      ...validationMessages,
      customValidation: t('tocco.registration.personalInfo.phoneFormatError'),
    }"
    :name="name"
    :handle-focus-fn="(f: boolean) => handleFocus(f)"
    :handle-validate-fn="handleValidate"
    :handle-phone-object-fn="(obj: any) => handlePhoneObject(obj)"
    @input="handleInput"
  />
</FormKitLazyProvider>

</template>

<style lang="scss" scoped></style>
